import { Reveal } from "@/lib/reveal";
import { Link } from "react-router-dom";
import { SERVICES } from "@/data/services";
import { ArrowRight, Check, Clock, CreditCard, Shield, Info } from "lucide-react";
import AmbientVideo from "@/components/AmbientVideo";
import { AMBIENT_VIDEOS, AMBIENT_POSTERS } from "@/data/media";
import { usePageMeta } from "@/lib/usePageMeta";

const INCLUDED_EVERYWHERE = [
  "Judgment-free conversation",
  "A calm, grounded presence",
  "Supportive companionship during errands, outings, and life transitions",
  "Flexible scheduling around your week",
];

export default function PricingPage() {
  usePageMeta({
    title: "Pricing — Honest, Gentle Session Rates | Calm & Connected",
    description:
      "Simple, transparent pricing for one-on-one peer support sessions: 30 minutes from $35, 60 minutes from $65, and 2-hour extended sessions from $120. No surprises.",
  });
  return (
    <div data-testid="pricing-page">
      {/* Header */}
      <section className="pt-36 md:pt-44 pb-12 relative overflow-hidden">
        <span aria-hidden className="cc-orb cc-orb-1" style={{ opacity: 0.3 }} />
        <div className="cc-container max-w-3xl relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              Pricing
            </p>
            <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.05]">
              Honest, gentle pricing.<br />
              <em className="italic text-[var(--cc-primary)]">No surprises.</em>
            </h1>
            <p className="mt-7 text-lg text-[var(--cc-text-soft)] leading-relaxed">
              Three simple session lengths so you can choose what fits the
              moment you&apos;re in. Pay only for the time we share — nothing
              hidden, nothing pushy.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Pricing Table */}
      <section className="pb-14 md:pb-20">
        <div className="cc-container">
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 100}>
                <div
                  data-testid={`pricing-card-${s.id}`}
                  className={`cc-card p-8 md:p-10 h-full relative flex flex-col transition-transform duration-500 hover:-translate-y-1 ${
                    s.featured ? "ring-1 ring-[var(--cc-primary)]/30" : ""
                  }`}
                >
                  {s.featured && (
                    <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[var(--cc-primary)] text-white text-[10px] uppercase tracking-[0.18em]">
                      Most chosen
                    </span>
                  )}
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--cc-text-soft)]">
                    <Clock size={13} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
                    {s.duration}
                  </div>
                  <h3 className="mt-4 font-serif text-2xl md:text-[1.7rem] leading-snug">
                    {s.name}
                  </h3>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-serif text-5xl md:text-6xl text-[var(--cc-text)]">
                      {s.price}
                    </span>
                    <span className="text-sm text-[var(--cc-text-soft)]">
                      / {s.duration.toLowerCase()}
                    </span>
                  </div>
                  <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
                    {s.blurb}
                  </p>
                  <div className="mt-auto pt-8">
                    <Link
                      to={`/booking?session=${s.id}`}
                      data-testid={`pricing-cta-${s.id}`}
                      className={
                        s.featured ? "cc-btn-primary w-full justify-center" : "cc-btn-secondary w-full justify-center"
                      }
                    >
                      Schedule a Conversation
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Calming video banner */}
      <section className="pb-16 md:pb-24">
        <div className="cc-container">
          <Reveal>
            <AmbientVideo
              src={AMBIENT_VIDEOS.landscape}
              poster={AMBIENT_POSTERS.landscape}
              overlay="medium"
              className="w-full h-[260px] md:h-[340px] cc-card"
              testId="pricing-ambient-video"
            >
              <div className="h-full flex items-center">
                <div
                  className="ml-6 md:ml-12 my-6 md:my-10 max-w-md px-7 py-6 md:px-8 md:py-7 rounded-3xl backdrop-blur-md border border-white/40 shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
                  style={{ background: "rgba(253, 251, 247, 0.88)" }}
                >
                  <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
                    A reminder
                  </p>
                  <h2 className="mt-3 font-serif text-2xl md:text-3xl leading-tight text-[var(--cc-text)]">
                    You&apos;re paying for time and presence — <em className="italic">not performance.</em>
                  </h2>
                </div>
              </div>
            </AmbientVideo>
          </Reveal>
        </div>
      </section>

      {/* What every session includes */}
      <section className="cc-section bg-[var(--cc-surface)]">
        <div className="cc-container grid md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.22em] text-[var(--cc-primary)]">
              Every session includes
            </p>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight">
              The same warm welcome, <em className="italic">always.</em>
            </h2>
            <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
              Whichever session length you choose, the heart of our time
              together stays the same.
            </p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <ul className="cc-card p-8 md:p-10 space-y-4">
              {INCLUDED_EVERYWHERE.map((item) => (
                <li key={item} className="flex items-start gap-3 text-[var(--cc-text)]">
                  <Check size={18} strokeWidth={1.7} className="mt-1 text-[var(--cc-primary)] shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Payment + Billing notes */}
      <section className="cc-section">
        <div className="cc-container grid md:grid-cols-3 gap-6">
          <Reveal>
            <div className="cc-card p-7 h-full">
              <div className="w-11 h-11 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center">
                <CreditCard size={17} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
              </div>
              <h3 className="mt-5 font-serif text-2xl">Online payments — coming soon</h3>
              <p className="mt-3 text-[var(--cc-text-soft)] leading-relaxed">
                Secure card payments will be available directly through the
                booking page shortly. For now, we&apos;ll confirm details together
                when we connect.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="cc-card p-7 h-full">
              <div className="w-11 h-11 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center">
                <Shield size={17} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
              </div>
              <h3 className="mt-5 font-serif text-2xl">Cancellations are okay</h3>
              <p className="mt-3 text-[var(--cc-text-soft)] leading-relaxed">
                Life happens. Please give as much notice as you can and we&apos;ll
                find a new time. No guilt, no penalty for genuine reschedules.
              </p>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="cc-card p-7 h-full">
              <div className="w-11 h-11 rounded-full bg-[var(--cc-surface)] border border-[var(--cc-line)] flex items-center justify-center">
                <Info size={17} strokeWidth={1.5} className="text-[var(--cc-primary)]" />
              </div>
              <h3 className="mt-5 font-serif text-2xl">Need something different?</h3>
              <p className="mt-3 text-[var(--cc-text-soft)] leading-relaxed">
                Recurring weekly check-ins, packages, or longer outings can be
                arranged — just reach out and we&apos;ll shape something that
                fits.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="cc-section bg-[var(--cc-surface)]">
        <div className="cc-container text-center max-w-2xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Not sure which fits? Let&apos;s figure it out together.
            </h2>
            <p className="mt-5 text-[var(--cc-text-soft)] leading-relaxed">
              Send a short note — even a sentence is enough. I&apos;ll help you
              pick the option that feels right.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <Link to="/booking" data-testid="pricing-cta-primary" className="cc-btn-primary">
                Schedule a Conversation <ArrowRight size={16} strokeWidth={1.7} />
              </Link>
              <Link to="/contact" data-testid="pricing-cta-secondary" className="cc-btn-secondary">
                Send a Note
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
